import { useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";

import type { ProviderPortalRequestListItemSchema } from "@api/generated/types.gen";

import { useProviderRequests } from "@hooks/queries/use-provider-portal";

import { formatNullableDateToLocal } from "@utils/dateUtils";
import { cn } from "@utils/ui";

// Request lifecycle → design pill.
const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  DRAFT: { label: "Draft", cls: "bg-[#eef1f6] text-[#5A5A6B]" },
  SUBMITTED: { label: "New", cls: "bg-[#e6f0fb] text-[#0065CB]" },
  IN_PROGRESS: { label: "In progress", cls: "bg-[#fdf0dc] text-[#B26A00]" },
  COMPLETED: { label: "Complete", cls: "bg-[#e7f6ec] text-[#16794F]" },
  FAILED: { label: "Failed", cls: "bg-[#fdecec] text-[#C0392B]" },
};

const REQUEST_TYPE_LABEL: Record<string, string> = {
  records: "Records",
  medical_report: "Medical report",
  work_capacity: "Work capacity",
};

const TABS = ["All", "Open", "Complete"] as const;
type Tab = (typeof TABS)[number];

const th =
  "px-3 py-3 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-[#5A5A6B]";
const td = "px-3 py-[13px] text-[12.5px] text-[#5A5A6B] align-middle";

const isOpen = (s: string) => s !== "COMPLETED" && s !== "FAILED";

/**
 * Provider portal — Requests (MDRS-157). The document requests referrers raised
 * to this provider (server-scoped to the provider's identity). Rows open the
 * request detail. Mirrors the referrer Cases table styling; the dark portal
 * chrome comes from the shell.
 */
const ProviderRequestsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("All");
  const [search, setSearch] = useState("");

  const { data: requests = [], isLoading } = useProviderRequests();

  const counts = useMemo(() => {
    const open = requests.filter((r) => isOpen(r.status)).length;
    return { All: requests.length, Open: open, Complete: requests.length - open };
  }, [requests]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (tab === "Open" && !isOpen(r.status)) return false;
      if (tab === "Complete" && isOpen(r.status)) return false;
      if (!q) return true;
      return [r.reference_number, r.claim_number, r.claimant, r.referrer]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q));
    });
  }, [requests, tab, search]);

  const open = (r: ProviderPortalRequestListItemSchema) =>
    navigate({ to: `/provider/requests/${r.id}` });

  return (
    <div className="min-h-full bg-[#F5F7FA] px-[34px] py-[26px]">
      <div className="mb-[18px] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.01em] text-[#1A1939]">
            Cases
          </h1>
          <p className="text-sm text-[#5A5A6B]">
            Every record request raised by a referrer through MEDirect. Open one to
            work the retrieval and return it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: "/provider/requests/new" })}
          className="flex items-center gap-2 rounded-[10px] bg-[#0065CB] px-[18px] py-2.5 text-[13.5px] font-semibold text-white shadow-[0_2px_8px_rgba(0,101,203,0.28)]"
        >
          <Plus className="h-[15px] w-[15px]" /> Log request
        </button>
      </div>

      {/* Filters + search */}
      <div className="mb-4 flex flex-wrap items-center gap-3.5">
        <div className="flex items-center gap-1.5 rounded-[11px] border border-[#E2E5EB] bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "flex items-center gap-[7px] rounded-lg px-3.5 py-[7px] text-[13px] font-semibold",
                tab === t
                  ? "bg-[#e8f2fb] text-[#0065CB]"
                  : "text-[#5A5A6B] hover:bg-[#f7f9fc]"
              )}
            >
              {t}
              <span className="text-[11px] font-bold opacity-75">{counts[t]}</span>
            </button>
          ))}
        </div>
        <div className="relative flex min-w-[220px] max-w-[360px] flex-1 items-center">
          <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[#8A8A98]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by request, claim, claimant or referrer"
            className="w-full rounded-[10px] border border-[#E2E5EB] bg-white py-[9px] pl-[34px] pr-3 text-[13.5px] outline-none focus:border-[#0065CB]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-[14px] border border-[#E2E5EB] bg-white shadow-[0_2px_8px_rgba(26,25,57,0.05)]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] border-collapse">
            <thead>
              <tr className="border-b border-[#E2E5EB] bg-[#fafbfd]">
                <th className={cn(th, "pl-[18px]")}>Request</th>
                <th className={th}>Claimant</th>
                <th className={th}>Referrer</th>
                <th className={th}>Request type</th>
                <th className={th}>Received</th>
                <th className={th}>Due</th>
                <th className={cn(th, "pr-[18px]")}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pill = STATUS_PILL[r.status] ?? STATUS_PILL.SUBMITTED;
                return (
                  <tr
                    key={r.id}
                    onClick={() => open(r)}
                    className="cursor-pointer border-b border-[#F0F2F6] last:border-0 hover:bg-[#f9fbfe]"
                  >
                    <td className={cn(td, "pl-[18px]")}>
                      <div className="font-bold text-[#0065CB]">
                        {r.reference_number ?? "—"}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-[#8A8A98]">
                        {r.claim_number ?? "—"}
                      </div>
                    </td>
                    <td className={cn(td, "font-medium text-[#1A1939]")}>
                      {r.claimant ?? "—"}
                    </td>
                    <td className={td}>{r.referrer ?? "—"}</td>
                    <td className={td}>
                      {REQUEST_TYPE_LABEL[r.request_type] ?? r.request_type}
                    </td>
                    <td className={td}>
                      {r.received_count}/{r.total_count}
                    </td>
                    <td className={td}>
                      {formatNullableDateToLocal(r.due_date, "MMM d, yyyy", "—")}
                    </td>
                    <td className={cn(td, "pr-[18px]")}>
                      <span
                        className={cn(
                          "inline-block rounded-full px-2.5 py-1 text-[11px] font-bold",
                          pill.cls
                        )}
                      >
                        {pill.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-[34px] text-center text-[13px] text-[#8A8A98]"
                  >
                    No cases match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProviderRequestsPage;
